import { useEffect, useState } from "react";

import { adminUiCopy } from "@/copy/adminUiCopy";
import {
  clearStoredPreviewEmailOverride,
  getConfiguredPreviewEmailOverride,
  getStoredPreviewEmailOverride,
  isPreviewEmailOverrideEnvironment,
  PREVIEW_EMAIL_OVERRIDE_OPEN_EVENT,
  setStoredPreviewEmailOverride,
} from "@/utils/previewEmailOverride";
import {
  VNextButton,
  VNextDialog,
  VNextField,
  VNextInput,
  VNextNotice,
} from "@/vnext/primitives";
import "@/vnext/theme/vnext.css";

const normalizeInputEmail = (value: string): string =>
  value.trim().toLowerCase();

const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export const PreviewEmailOverrideGate = () => {
  const isOverrideRequiredEnvironment = isPreviewEmailOverrideEnvironment();
  const resolveEffectiveOverride = () => getStoredPreviewEmailOverride();
  const configuredOverride = getConfiguredPreviewEmailOverride();
  const storedOverride = resolveEffectiveOverride();
  const [open, setOpen] = useState<boolean>(false);

  const [value, setValue] = useState<string>(() => storedOverride ?? "");
  const [error, setError] = useState("");

  const handleSubmit = () => {
    const normalized = normalizeInputEmail(value);
    if (!isValidEmail(normalized)) {
      setError(adminUiCopy.previewEmailGate.invalidEmail);
      return;
    }

    setStoredPreviewEmailOverride(normalized);
    setOpen(false);
    setError("");
  };

  const handleReset = () => {
    clearStoredPreviewEmailOverride();
    const fallbackOverride = resolveEffectiveOverride();
    setOpen(!fallbackOverride);
    setValue(fallbackOverride ?? "");
    setError("");
  };

  useEffect(() => {
    if (typeof window === "undefined" || !isOverrideRequiredEnvironment) {
      return undefined;
    }

    const openDialog = () => {
      setValue(resolveEffectiveOverride() ?? "");
      setError("");
      setOpen(true);
    };

    window.addEventListener(PREVIEW_EMAIL_OVERRIDE_OPEN_EVENT, openDialog);

    return () => {
      window.removeEventListener(PREVIEW_EMAIL_OVERRIDE_OPEN_EVENT, openDialog);
    };
  }, [isOverrideRequiredEnvironment]);

  if (!isOverrideRequiredEnvironment || !open) {
    return null;
  }

  const isBlocking = !resolveEffectiveOverride();
  const resetButtonLabel = configuredOverride
    ? adminUiCopy.previewEmailGate.resetToDefault
    : adminUiCopy.previewEmailGate.clearStored;

  return (
    <VNextDialog
      description={adminUiCopy.previewEmailGate.warningText}
      footer={
        <>
          <VNextButton
            onClick={handleReset}
            title={adminUiCopy.hoverHelp.previewEmail.resetOverride}
            tone="quiet"
          >
            {resetButtonLabel}
          </VNextButton>
          <VNextButton onClick={handleSubmit} tone="primary">
            {adminUiCopy.previewEmailGate.saveOverride}
          </VNextButton>
        </>
      }
      onClose={() => {
        if (!isBlocking) {
          setOpen(false);
          setError("");
          setValue(resolveEffectiveOverride() ?? "");
        }
      }}
      open={open}
      title={adminUiCopy.previewEmailGate.title}
    >
      <div className="vnext-card__body">
        <VNextField
          error={error || null}
          hint={adminUiCopy.hoverHelp.previewEmail.testRecipient}
          label={adminUiCopy.previewEmailGate.recipientLabel}
        >
          <VNextInput
            aria-label={adminUiCopy.previewEmailGate.recipientLabel}
            autoFocus
            onChange={(event) => {
              setValue(event.target.value);
              setError("");
            }}
            type="email"
            value={value}
          />
        </VNextField>
        <VNextNotice tone="warning">
          {adminUiCopy.previewEmailGate.requiredNotice}
        </VNextNotice>
        <VNextNotice tone="info">
          {adminUiCopy.hoverHelp.previewEmail.resetOverride}
        </VNextNotice>
        {configuredOverride ? (
          <VNextNotice tone="info">
            {adminUiCopy.previewEmailGate.defaultOverridePrefix}{" "}
            {configuredOverride}
          </VNextNotice>
        ) : null}
      </div>
    </VNextDialog>
  );
};
